import Styles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, documentId, getDocs, getFirestore, limit, orderBy, query, startAfter, where,
  QueryDocumentSnapshot, DocumentData,
} from 'firebase/firestore';
import { FaSearch } from 'react-icons/fa';
import { useAppSelector } from 'src/salon/app/hook';
import PostLikeHeart from 'src/salon/package/postLikeHeart/index';
import StudentAccountPanel from './StudentAccountPanel';

type StudentPost = {
  id: string;
  postUid: string;
  postName: string;
  postTags: string[];
  companyLikes: string[];
};

type StudentInfo = {
  name: string;
  school: string;
  grade: string;
};

const PAGE_SIZE = 30;
const USERS_CHUNK = 30; // Firestore の documentId() 'in' クエリの上限

function toStudentPost(doc: QueryDocumentSnapshot<DocumentData>): StudentPost {
  const data = doc.data();
  return {
    id: doc.id,
    postUid: data.post_uid ?? '',
    postName: data.post_name ?? '',
    // 既存のタグ機能(post_tags)。古い投稿には無いため未設定時は[]として扱う。
    postTags: Array.isArray(data.post_tags) ? data.post_tags : [],
    // 古い投稿に companyLikes が無い場合は [] として扱う
    companyLikes: Array.isArray(data.companyLikes) ? data.companyLikes : [],
  };
}

// users/{uid} から、カード表示・検索・アカウント情報パネルに必要な最低限の項目だけを
// 取得する（15.evaluation/index.tsx の fetchSchoolsByUids と同じ、
// documentId() 'in' の分割取得パターン）。
async function fetchStudentInfoByUids(uids: string[]): Promise<Map<string, StudentInfo>> {
  const db = getFirestore();
  const map = new Map<string, StudentInfo>();
  const uniqueIds = Array.from(new Set(uids.filter(Boolean)));
  for (let i = 0; i < uniqueIds.length; i += USERS_CHUNK) {
    const chunk = uniqueIds.slice(i, i + USERS_CHUNK);
    if (chunk.length === 0) continue;
    const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
    snap.docs.forEach((d) => {
      const data = d.data();
      map.set(d.id, {
        name: data?.profile?.name ?? '',
        school: data?.education?.school ?? '',
        grade: data?.education?.grade ?? '',
      });
    });
  }
  return map;
}

function workImageUrl(postId: string): string {
  return `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/image%2Fresize_images%2F${postId}_500x500?alt=media&token=`;
}

// 学生全員の投稿作品を一覧表示し、いいねできる画面。
// 応募者に限らず、プラットフォーム上の学生作品（post_student: true）すべてが対象。
//
// 「学生の作品にいいねする」「作品を投稿した学生のアカウント情報を見る」の2つに絞り、
// 取得済みのFirestoreデータ（posts.companyLikes/post_tags、users.profile.name/education.*）
// だけでUIを構成する。新しいコレクション・フィールド・推薦ロジックは追加しない。
const StudentWorks: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);

  const [ posts, setPosts ] = useState<StudentPost[]>([]);
  const [ studentInfo, setStudentInfo ] = useState<Map<string, StudentInfo>>(new Map());
  const [ lastDoc, setLastDoc ] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [ hasMore, setHasMore ] = useState(true);
  const [ loading, setLoading ] = useState(true);
  const [ loadingMore, setLoadingMore ] = useState(false);
  const [ searchText, setSearchText ] = useState('');
  // 既存のいいねデータ(companyLikes)だけで判定できる絞り込み。
  // 「おすすめ」を判定できる既存データ・ロジックが無いため、そのタブは設置しない。
  const [ likedOnly, setLikedOnly ] = useState(false);
  const [ selectedPostId, setSelectedPostId ] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadFirstPage();
  }, []);

  async function mergeStudentInfo(list: StudentPost[]) {
    const info = await fetchStudentInfoByUids(list.map((p) => p.postUid));
    setStudentInfo((prev) => new Map([...prev, ...info]));
  }

  async function loadFirstPage() {
    setLoading(true);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'posts'),
        where('post_student', '==', true),
        orderBy('post_time', 'desc'),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const nextPosts = snap.docs.map(toStudentPost);
      setPosts(nextPosts);
      setLastDoc(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      await mergeStudentInfo(nextPosts);
    } catch (error) {
      console.error('studentWorks fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'posts'),
        where('post_student', '==', true),
        orderBy('post_time', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const nextPosts = snap.docs.map(toStudentPost);
      setPosts((prev) => [...prev, ...nextPosts]);
      setLastDoc(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : lastDoc);
      setHasMore(snap.docs.length === PAGE_SIZE);
      await mergeStudentInfo(nextPosts);
    } catch (error) {
      console.error('studentWorks loadMore failed:', error);
    } finally {
      setLoadingMore(false);
    }
  }

  function applyLikeChange(postId: string, nextLikes: string[]) {
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, companyLikes: nextLikes } : p));
  }

  // 一覧の取得元(chats等)にフリーワード検索の仕組みが既にある画面(15.evaluation/16.judging)と
  // 同様、サーバー検索は追加せず読み込み済みの一覧をクライアント側で絞り込む。
  const visiblePosts = useMemo(() => {
    let list = posts;
    if (likedOnly) {
      list = list.filter((p) => p.companyLikes.includes(company.id));
    }
    const text = searchText.trim();
    if (text) {
      list = list.filter((p) => {
        const info = studentInfo.get(p.postUid);
        const haystack = [p.postName, info?.name, info?.school, info?.grade, ...p.postTags].join(' ');
        return haystack.includes(text);
      });
    }
    return list;
  }, [posts, likedOnly, searchText, studentInfo, company.id]);

  const selectedPost = selectedPostId ? posts.find((p) => p.id === selectedPostId) ?? null : null;

  return (
    <div className={selectedPost ? Styles.workspace : Styles.card}>
      <div className={selectedPost ? Styles.workspaceList : undefined}>
        <div className={Styles.pageHeader}>
          <h2 className={Styles.pageTitle}>学生の作品</h2>
          <p className={Styles.pageDesc}>学生が投稿した作品を見て、気になる作品にいいねを送ることができます。</p>
        </div>

        <div className={Styles.filterBar}>
          <div className={Styles.searchBox}>
            <FaSearch className={Styles.searchIcon} />
            <input
              placeholder="学生名・学校名・作品名・タグで検索"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <label className={Styles.likedOnlyToggle}>
            <input type="checkbox" checked={likedOnly} onChange={(e) => setLikedOnly(e.target.checked)} />
            いいね済みのみ表示
          </label>
        </div>

        {loading ? (
          <p className={Styles.empty}>読み込み中...</p>
        ) : visiblePosts.length === 0 ? (
          <p className={Styles.empty}>
            {likedOnly ? 'いいね済みの作品はまだありません。' : '該当する作品が見つかりません。'}
          </p>
        ) : (
          <>
            <div className={Styles.grid}>
              {visiblePosts.map((post) => {
                const info = studentInfo.get(post.postUid);
                return (
                  <div className={Styles.item} key={post.id}>
                    <img
                      className={Styles.image}
                      src={workImageUrl(post.id)}
                      alt="美容学生作品撮り"
                    />

                    {post.postTags.length > 0 && (
                      <div className={Styles.tagRow}>
                        {post.postTags.slice(0, 3).map((tag) => (
                          <span className={Styles.tag} key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className={Styles.meta}>
                      <p className={Styles.studentName}>{info?.name || '名前未設定'}</p>
                    </div>

                    <div className={Styles.actions}>
                      <PostLikeHeart
                        postId={post.id}
                        postUid={post.postUid}
                        companyLikes={post.companyLikes}
                        onToggled={(next) => applyLikeChange(post.id, next)}
                        variant="button"
                      />
                      <button
                        type="button"
                        className={Styles.accountBtn}
                        onClick={() => setSelectedPostId(post.id)}
                      >
                        アカウント情報を見る
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <button className={Styles.more} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? '読み込み中...' : 'もっと見る'}
              </button>
            )}
          </>
        )}
      </div>

      {selectedPost && (
        <div className={Styles.workspaceDrawer}>
          <StudentAccountPanel
            postId={selectedPost.id}
            postUid={selectedPost.postUid}
            companyLikes={selectedPost.companyLikes}
            onClose={() => setSelectedPostId(null)}
            onToggled={applyLikeChange}
          />
        </div>
      )}
    </div>
  );
};

export default StudentWorks;
